import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ShoppingCart, Heart, Share2, ChevronLeft, Shield, Truck, Package } from 'lucide-react';
import { motion } from 'framer-motion';
import { useCart } from '../context/CartContext';
import { allProducts } from '../data/products';
import ProductCard from '../components/ProductCard';
import { visibleCategory } from '../utils/reportingCategory';

const ProductDetail = () => {
  const { id } = useParams();
  const { addToCart } = useCart();
  const [quantity, setQuantity] = useState(1);
  const [selectedTab, setSelectedTab] = useState('description');

  const product = allProducts.find(p => p.id === parseInt(id));

  if (!product) {
    return (
      <div className="min-h-screen bg-gray-50 pt-24">
        <div className="container">
          <div className="text-center py-16">
            <h1 className="text-3xl font-bold mb-4">Product Not Found</h1>
            <Link to="/shop" className="text-primary-600 hover:text-primary-700">
              Return to Shop
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const relatedProducts = allProducts
    .filter(p => p.category === product.category && p.id !== product.id)
    .slice(0, 4);

  const handleAddToCart = () => {
    for (let i = 0; i < quantity; i++) {
      addToCart(product);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-24 pb-12">
      <div className="container">
        {/* Breadcrumb */}
        <nav className="flex items-center space-x-2 text-sm text-gray-600 mb-6">
          <Link to="/shop" className="hover:text-primary-600 flex items-center">
            <ChevronLeft className="w-4 h-4" />
            Shop
          </Link>
          {visibleCategory(product.category) && (
            <>
              <span>/</span>
              <Link to={`/shop/${product.category.toLowerCase()}`} className="hover:text-primary-600">
                {product.category}
              </Link>
            </>
          )}
          <span>/</span>
          <span className="text-gray-900">{product.name}</span>
        </nav>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Product Image */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="aspect-square bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg flex items-center justify-center text-[150px]">
                {product.image}
              </div>
            </div>
          </motion.div>

          {/* Product Info */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <div className="mb-6">
              {/* Badges */}
              <div className="flex gap-2 mb-4">
                {product.isNew && (
                  <span className="bg-green-100 text-green-700 text-xs px-3 py-1 rounded-full font-semibold">
                    NEW ARRIVAL
                  </span>
                )}
                {product.isTrending && (
                  <span className="bg-orange-100 text-orange-700 text-xs px-3 py-1 rounded-full font-semibold">
                    TRENDING
                  </span>
                )}
                {product.stock && product.stock < 10 && (
                  <span className="bg-red-100 text-red-700 text-xs px-3 py-1 rounded-full font-semibold">
                    LOW STOCK
                  </span>
                )}
              </div>

              <h1 className="text-3xl font-bold mb-2">{product.name}</h1>
              {product.set && (
                <p className="text-lg text-gray-600 mb-4">{product.set}</p>
              )}

              {/* Price */}
              <div className="mb-6">
                {product.originalPrice && product.originalPrice > product.price && (
                  <span className="text-xl text-gray-400 line-through mr-3">
                    ${product.originalPrice.toFixed(2)}
                  </span>
                )}
                <span className="text-3xl font-bold text-gray-900">
                  ${product.price.toFixed(2)}
                </span>
                {product.discount && (
                  <span className="ml-3 bg-red-100 text-red-700 px-3 py-1 rounded-lg font-semibold">
                    Save {product.discount}%
                  </span>
                )}
              </div>

              {/* Stock Status */}
              {product.stock && (
                <div className="mb-6">
                  <p className={`text-sm font-medium ${product.stock < 10 ? 'text-red-600' : 'text-green-600'}`}>
                    {product.stock < 10 
                      ? `Only ${product.stock} left in stock!` 
                      : `${product.stock} in stock`}
                  </p>
                </div>
              )}

              {/* Quantity & Add to Cart */}
              <div className="flex gap-4 mb-6">
                <div className="flex items-center border border-gray-300 rounded-lg">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="px-4 py-3 hover:bg-gray-100"
                  >
                    -
                  </button>
                  <input
                    type="number"
                    value={quantity}
                    onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
                    className="w-16 text-center border-x border-gray-300"
                  />
                  <button
                    onClick={() => setQuantity(quantity + 1)}
                    className="px-4 py-3 hover:bg-gray-100"
                  >
                    +
                  </button>
                </div>
                <button
                  onClick={handleAddToCart}
                  className="flex-1 btn-primary flex items-center justify-center"
                >
                  <ShoppingCart className="w-5 h-5 mr-2" />
                  Add to Cart
                </button>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 mb-8">
                <button className="flex-1 flex items-center justify-center py-3 border border-gray-300 rounded-lg hover:bg-gray-50">
                  <Heart className="w-5 h-5 mr-2" />
                  Add to Wishlist
                </button>
                <button className="flex-1 flex items-center justify-center py-3 border border-gray-300 rounded-lg hover:bg-gray-50">
                  <Share2 className="w-5 h-5 mr-2" />
                  Share
                </button>
              </div>

            </div>
          </motion.div>
        </div>

        {/* Product Tabs */}
        <div className="mt-12">
          <div className="border-b border-gray-200">
            <div className="flex space-x-8">
              {['description', 'details', 'shipping'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setSelectedTab(tab)}
                  className={`py-3 px-1 border-b-2 font-medium text-sm capitalize transition-colors ${
                    selectedTab === tab
                      ? 'border-primary-600 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {tab}
                </button>
              ))}
            </div>
          </div>

          <div className="py-8">
            {selectedTab === 'description' && (
              <div className="prose max-w-none">
                <p className="text-gray-600">
                  Add this amazing {product.name} to your collection! This card from the {product.set} set 
                  is a must-have for any serious collector or competitive player.
                </p>
                <h3 className="text-lg font-semibold mt-4 mb-2">Card Features</h3>
                <ul className="list-disc list-inside text-gray-600 space-y-1">
                  <li>Official tournament legal card</li>
                  <li>Mint/Near Mint condition</li>
                  <li>Protective sleeve included</li>
                  <li>Authenticated and verified</li>
                </ul>
              </div>
            )}
            {selectedTab === 'details' && (
              <div className="space-y-2">
                {visibleCategory(product.category) && (
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-gray-600">Category</span>
                    <span className="font-medium">{product.category}</span>
                  </div>
                )}
                {product.set && (
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-gray-600">Set</span>
                    <span className="font-medium">{product.set}</span>
                  </div>
                )}
                <div className="flex justify-between py-2 border-b">
                  <span className="text-gray-600">Condition</span>
                  <span className="font-medium">Near Mint</span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span className="text-gray-600">Language</span>
                  <span className="font-medium">English</span>
                </div>
              </div>
            )}
            {selectedTab === 'shipping' && (
              <div className="text-gray-600">
                <p className="mb-4">
                  We ship all orders within 1-2 business days. Cards are carefully packaged in protective 
                  sleeves and rigid mailers to ensure they arrive in perfect condition.
                </p>
                <h3 className="font-semibold mb-2">Shipping Options:</h3>
                <ul className="list-disc list-inside space-y-1">
                  <li>Standard Shipping (5-7 days): $4.99</li>
                  <li>Express Shipping (2-3 days): $12.99</li>
                  <li>Overnight Shipping: $24.99</li>
                  <li>Local Pickup: FREE</li>
                </ul>
              </div>
            )}
          </div>
        </div>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <div className="mt-12">
            <h2 className="text-2xl font-bold mb-6">You May Also Like</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {relatedProducts.map((relatedProduct) => (
                <ProductCard key={relatedProduct.id} product={relatedProduct} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductDetail;
